import { useEffect } from 'react';

// Reloads the page when the server is serving a newer build than the one this
// page is running.
//
// This exists for the phones. Plenty of people open the calendar from an "Add
// to Home Screen" shortcut, and when iOS resumes one of those from the app
// switcher it restores the web view straight out of memory: no navigation, no
// request, so index.html is never revalidated and the device can sit on a
// months-old bundle indefinitely. There is no cache lifetime to wait out,
// because nothing is asking. Timers and visibilitychange do resume, so those
// are the openings we have.

// One reload attempt per target build. A reload that fails to land on the new
// bundle (a stale proxy handing out old HTML alongside a fresh manifest, say)
// would otherwise reload forever and hammer the server.
const RELOAD_MARKER = 'buildVersionReloadTarget';

// The bundle actually executing. CRA emits exactly one
// <script src="/static/js/main.<contenthash>.js">, and that hash changes with
// every build whose output differs, so it doubles as a build id.
function runningBundle() {
  const script = document.querySelector('script[src*="/static/js/main."]');
  return script ? new URL(script.src, window.location.href).pathname : null;
}

// Same origin as the page, so this deliberately ignores REACT_APP_API_BASE_URL:
// the manifest is served by the client's own nginx, not by the API.
async function deployedBundle() {
  // no-store rather than no-cache: asset-manifest.json matches neither of
  // nginx's location blocks, so it goes out with no Cache-Control header at all
  // and the browser may heuristically cache it. A stale answer here would
  // defeat the entire check.
  const res = await fetch('/asset-manifest.json', { cache: 'no-store' });
  if (!res.ok) throw new Error(`asset manifest: ${res.status}`);
  const manifest = await res.json();
  const main = manifest?.files?.['main.js'];
  return main ? new URL(main, window.location.href).pathname : null;
}

// Private mode can make sessionStorage throw on access, and a version check is
// never worth taking the app down over.
function readMarker() {
  try {
    return sessionStorage.getItem(RELOAD_MARKER);
  } catch {
    return null;
  }
}

function writeMarker(target) {
  try {
    sessionStorage.setItem(RELOAD_MARKER, target);
  } catch {
    // Nothing to do. Worst case is one extra reload attempt later.
  }
}

export function useBuildVersionCheck({ intervalMs = 60000 } = {}) {
  useEffect(() => {
    const booted = runningBundle();
    // The dev server serves an unhashed bundle, so there is nothing to compare.
    if (!booted) return undefined;

    let stopped = false;

    async function check() {
      if (stopped || document.visibilityState === 'hidden') return;

      let deployed;
      try {
        deployed = await deployedBundle();
      } catch {
        return; // Offline, or a blip. The next tick tries again.
      }

      if (stopped || !deployed || deployed === booted) return;
      if (readMarker() === deployed) return;

      writeMarker(deployed);
      window.location.reload();
    }

    // iOS wakes a resumed home-screen app without navigating anywhere, so this
    // is the first moment we can notice the page has been sitting on old code.
    function onVisibilityChange() {
      if (document.visibilityState === 'visible') check();
    }

    document.addEventListener('visibilitychange', onVisibilityChange);
    const interval = setInterval(check, intervalMs);
    check();

    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      clearInterval(interval);
    };
  }, [intervalMs]);
}
